import { alpha, useTheme } from '@mui/material/styles'
import type { MessageType } from '../types/chat.types'
import { formatTimestamp } from '../utils/formatMessage'
import { useChat } from '../context/useChat'

const getInitials = (name?: string) => {
  const safeName = name?.trim() || '?'
  return safeName
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
}

interface MessageItemProps {
  message: MessageType
}

const StatusIcon = ({ status }: { status?: MessageType['status'] }) => {
  if (status === 'sending') {
    return (
      <svg className="h-3.5 w-3.5 text-slate-300" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    )
  }
  if (status === 'failed') {
    return (
      <svg className="h-3.5 w-3.5 text-rose-400" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }
  if (status === 'read') {
    return (
      <span className="text-[12px] font-semibold leading-none text-sky-500">
        ✓✓
      </span>
    )
  }
  if (status === 'delivered') {
    return (
      <span className="text-[12px] font-semibold leading-none text-slate-400">
        ✓✓
      </span>
    )
  }
  return (
    <span className="text-[12px] font-semibold leading-none text-slate-400">
      ✓
    </span>
  )
}

export const MessageItem = ({ message }: MessageItemProps) => {
  const theme = useTheme()
  const {
    participantDisplayName,
    participantAvatarUrl,
  } = useChat()
  const isMine = message.direction === 'outbound'
  const timeLabel = formatTimestamp(message.createdAt)
  const inboundName = participantDisplayName ?? 'Participant'

  return (
    <div
      className={`chat-msg-enter flex w-full items-end gap-2.5 ${
        isMine ? 'flex-row-reverse' : ''
      }`}
    >
      {!isMine && (
        participantAvatarUrl ? (
          <img
            src={participantAvatarUrl}
            alt={inboundName}
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover shadow-sm"
          />
        ) : (
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold shadow-sm"
            style={{
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            {getInitials(inboundName)}
          </div>
        )
      )}

      <div
        className={`flex max-w-[65%] flex-col ${
          isMine ? 'items-end' : 'items-start'
        }`}
      >
        <div
          className={`px-4 py-2.5 text-[14px] leading-relaxed shadow-sm ${
            isMine
              ? 'rounded-2xl rounded-br-md'
              : 'rounded-2xl rounded-bl-md'
          }`}
          style={
            isMine
              ? {
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: theme.palette.primary.contrastText,
                }
              : {
                  backgroundColor: '#F0F2F5',
                  color: theme.palette.text.primary,
                }
          }
        >
          {message.body}
        </div>

        <div
          className={`mt-1 flex items-center gap-1.5 text-[11px] text-slate-400 ${
            isMine ? 'flex-row-reverse' : ''
          }`}
        >
          <span>{timeLabel}</span>
          {isMine && <StatusIcon status={message.status} />}
        </div>
      </div>
    </div>
  )
}
